import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import AppShell from '../components/AppShell';
import { useAuth } from '../context/AuthContext';
import { useSurvey } from '../context/SurveyContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function fmt$(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);
}

function fmtDate(s) {
  if (!s) return 'Not set yet';
  const d = new Date(s + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

// ── Credit Request Modal ────────────────────────────────────────────────────────
function CreditRequestModal({ onSubmit, onClose, loading }) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  const reasons = [
  'Foundation issues found in appraisal package',
  'Roof damage or aging roof',
  'HVAC system needs replacement',
  'Plumbing issues discovered',
  'Electrical system concerns',
  'Appraisal gap',
  'Other repair costs',
  'Other',
  ];

  const handleSubmit = (e) => {
  e.preventDefault();
  if (!amount || !reason) return;
  onSubmit({ amount: parseFloat(amount), reason, notes });
  };

  return (
  <div className="safe-modal-overlay fixed inset-0 z-50 flex items-center justify-center px-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
  <div className="safe-modal-box bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 overflow-y-auto" style={{ '--sm-max': '90vh' }}>
  <div className="flex items-center justify-between mb-5">
  <h3 className="text-lg font-bold text-black">Request Seller Credit</h3>
  <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 text-gray-400">
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
  </button>
  </div>
  <p className="text-sm text-gray-500 mb-5">
  Request a credit from the seller based on findings in your appraisal package. Your advisor will negotiate on your behalf.
  </p>
  <form onSubmit={handleSubmit} className="space-y-4">
  <div>
  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Credit Amount</label>
  <div className="relative">
  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">$</span>
  <input
  type="number"
  value={amount}
  onChange={(e) => setAmount(e.target.value)}
  placeholder="5000"
  required
  min="1"
  className="w-full border border-gray-200 rounded-xl pl-8 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
  />
  </div>
  </div>
  <div>
  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Reason</label>
  <select
  value={reason}
  onChange={(e) => setReason(e.target.value)}
  required
  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white"
  >
  <option value="">Select a reason…</option>
  {reasons.map((r) => <option key={r} value={r}>{r}</option>)}
  </select>
  </div>
  <div>
  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Additional Notes (optional)</label>
  <textarea
  value={notes}
  onChange={(e) => setNotes(e.target.value)}
  rows={3}
  placeholder="Any specific details from your appraisal package report…"
  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
  />
  </div>
  <div className="flex gap-3 pt-1">
  <button type="button" onClick={onClose} className="flex-1 border border-gray-200 text-gray-600 rounded-xl py-2.5 text-sm font-semibold hover:bg-gray-50 transition-colors">
  Cancel
  </button>
  <button
  type="submit"
  disabled={loading || !amount || !reason}
  className="flex-1 text-white rounded-xl py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
  style={{ background: 'linear-gradient(135deg, #6d28d9 0%, #4f46e5 100%)' }}
  >
  {loading ? 'Submitting…' : 'Submit Request'}
  </button>
  </div>
  </form>
  </div>
  </div>
  );
}

// ── Task Card ──────────────────────────────────────────────────────────────────
function TaskCard({ number, title, subtitle, status, children }) {
  const statusStyle = {
  complete: { dot: 'bg-green-500', label: 'Complete', labelColor: 'text-green-600', bg: 'bg-green-50 border-green-200' },
  paid: { dot: 'bg-yellow-400', label: 'In Progress', labelColor: 'text-yellow-600', bg: 'bg-yellow-50 border-yellow-200' },
  unpaid: { dot: 'bg-gray-300', label: 'Pending', labelColor: 'text-gray-400', bg: 'bg-white border-gray-100' },
  pending: { dot: 'bg-gray-300', label: 'Pending', labelColor: 'text-gray-400', bg: 'bg-white border-gray-100' },
  locked: { dot: 'bg-gray-200', label: 'Locked', labelColor: 'text-gray-300', bg: 'bg-white border-gray-100' },
  };
  const s = statusStyle[status] || statusStyle.pending;

  return (
  <div className={`rounded-2xl border shadow-sm p-5 sm:p-6 transition-colors ${s.bg}`}>
  <div className="flex items-start gap-4 mb-4">
  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
  status === 'complete' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-500'
  }`}>
  {status === 'complete' ? (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
  </svg>
  ) : number}
  </div>
  <div className="flex-1 min-w-0">
  <div className="flex items-center gap-2 flex-wrap">
  <h3 className="font-bold text-black text-sm">{title}</h3>
  <span className={`inline-flex items-center gap-1 text-xs font-semibold ${s.labelColor}`}>
  <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
  {s.label}
  </span>
  </div>
  {subtitle && <p className="text-gray-500 text-xs mt-0.5">{subtitle}</p>}
  </div>
  </div>
  {children}
  </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
// The standalone /portal/option-period page now redirects to the dashboard,
// whose HeroOptionPeriod is a strict superset of this page (it adds the
// down-payment wire and PSA signing this page never had) and hits the same
// backend endpoints for review/decision/credit/AI-summary. Route + component
// kept in place for trivial revertibility — make OptionPeriodPage the default
// export again to restore the standalone page.
export default function OptionPeriod() {
  return <Navigate to="/dashboard" replace />;
}

export function OptionPeriodPage() {
  const { token } = useAuth();
  const { triggerSurvey } = useSurvey();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [credits, setCredits] = useState([]);
  const [emd, setEmd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiSummary, setAiSummary] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [modal, setModal] = useState(null); // 'credit' | null
  const [submitting, setSubmitting] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [deciding, setDeciding] = useState(null); // 'move_forward' | 'terminate'
  const [toast, setToast] = useState('');

  const showToast = (msg) => {
  setToast(msg);
  setTimeout(() => setToast(''), 3000);
  };

  const load = useCallback(() => {
  if (!token) return;
  axios
  .get(`${API_URL}/api/option-period/my`, { headers: { Authorization: `Bearer ${token}` } })
  .then((r) => {
  setRecord(r.data.record);
  setCredits(r.data.creditRequests || []);
  setEmd({
    borrowerPurchasePrice: r.data.borrower_purchase_price || 0,
    emdTotal: r.data.emd_total || 0,
    offerFeeCredit: r.data.offer_fee_credit || 0,
    emdRemaining: r.data.emd_remaining || 0,
  });
  })
  .catch(() => { setRecord(null); setCredits([]); setEmd(null); })
  .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const fetchAiSummary = useCallback(() => {
  if (!token || !record) return;
  setAiLoading(true);
  axios
  .get(`${API_URL}/api/option-period/ai-summary`, { headers: { Authorization: `Bearer ${token}` } })
  .then((r) => setAiSummary(r.data.summary))
  .catch(() => setAiSummary('Unable to generate summary at this time.'))
  .finally(() => setAiLoading(false));
  }, [token, record]);

  const handleReviewComplete = async () => {
  setReviewing(true);
  try {
  await axios.post(`${API_URL}/api/option-period/review-complete`, {}, { headers: { Authorization: `Bearer ${token}` } });
  showToast('Reports marked as reviewed!');
  load();
  } catch {
  showToast('Error. Please try again.');
  } finally {
  setReviewing(false);
  }
  };

  const handleCreditSubmit = async ({ amount, reason, notes }) => {
  setSubmitting(true);
  try {
  await axios.post(`${API_URL}/api/option-period/credit-request`, { amount, reason, notes }, { headers: { Authorization: `Bearer ${token}` } });
  showToast('Credit request submitted!');
  setModal(null);
  load();
  } catch {
  showToast('Error submitting request. Please try again.');
  } finally {
  setSubmitting(false);
  }
  };

  const handleDecision = async (decision) => {
  setDeciding(decision);
  try {
  await axios.post(`${API_URL}/api/option-period/decision`, { decision }, { headers: { Authorization: `Bearer ${token}` } });
  if (decision === 'move_forward') {
  showToast('Moving to Closing congratulations!');
  setTimeout(() => triggerSurvey('post_option_period'), 5000);
  setTimeout(() => navigate('/dashboard'), 1500);
  } else {
  showToast('Contract cancellation requested. Your advisor will be in touch.');
  setTimeout(() => triggerSurvey('post_option_period'), 5000);
  }
  load();
  } catch {
  showToast('Error. Please try again.');
  } finally {
  setDeciding(null);
  }
  };

  const appraisalStatus = record?.appraisal_status || 'unpaid';
  const inspectionStatus = record?.inspection_status || 'unpaid';
  const reviewStatus = record?.report_review_status || 'pending';
  const bothReportsReady = appraisalStatus === 'complete' && inspectionStatus === 'complete';
  const reviewLocked = !bothReportsReady;
  const creditLocked = reviewStatus !== 'complete';
  // GATES PROCEED ONLY. Never terminate. A borrower must be able to walk away at
  // any point during their option period — that right IS the option period, and
  // blocking it is worse than any gap gating protects against.
  // This was `decisionLocked` and wrapped BOTH buttons, so a borrower whose
  // report review was still pending could not cancel their contract.
  // Matches the server gate in routes/option-period.js exactly: the lock only
  // applies once a report has actually been DELIVERED. Locking on review status
  // alone trapped every borrower whose reports Doorly had not produced yet —
  // they could not proceed, and the option clock kept running.
  const reportsDelivered = appraisalStatus === 'complete' || inspectionStatus === 'complete';
  const proceedLocked = reportsDelivered && reviewStatus !== 'complete';
  const alreadyDecided = !!record?.decision;

  return (
  <AppShell>
  {/* Toast */}
  {toast && (
  <div className="fixed top-4 right-4 z-50 bg-black text-white px-5 py-3 rounded-xl text-sm font-medium shadow-lg">
  {toast}
  </div>
  )}

  {/* Modals */}
  {modal === 'credit' && (
  <CreditRequestModal
  onSubmit={handleCreditSubmit}
  onClose={() => setModal(null)}
  loading={submitting}
  />
  )}

  {loading ? (
  <div className="flex items-center justify-center py-24">
  <svg className="animate-spin w-8 h-8 text-[#3a6ad9]" fill="none" viewBox="0 0 24 24">
  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
  </div>
  ) : (
  <div className="max-w-3xl mx-auto">
  {/* Header */}
  <div className="rounded-2xl overflow-hidden mb-8" style={{ background: 'linear-gradient(135deg, #6d28d9 0%, #4f46e5 100%)' }}>
  <div className="px-6 py-7 sm:px-8">
  <div className="inline-flex items-center gap-2 bg-white/20 text-white text-xs font-semibold px-3 py-1 rounded-full mb-3 uppercase tracking-wider">
  Stage 9 of 10 Option Period
  </div>
  <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1.5">
  Option Period Tasks
  </h1>
  {record && (
  <p className="text-purple-200 text-sm">
  {record.property_address} &nbsp;·&nbsp; {fmt$(record.purchase_price)}
  {record.option_end_date && ` · Deadline: ${fmtDate(record.option_end_date)}`}
  </p>
  )}
  </div>
  </div>

  {!record && (
  <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6 mb-6">
  <p className="text-yellow-800 font-semibold text-sm">Your option period details haven't been set up yet.</p>
  <p className="text-yellow-700 text-sm mt-1">Your Doorly advisor will configure this shortly.</p>
  </div>
  )}

  {/* Down Payment summary — full down payment is wired up front; the
      $500 Purchase Initiation Deposit is no longer subtracted here. It surfaces
      as a lender credit on the Closing Disclosure (offer_fee_lender_credit_applied). */}
  {emd && emd.emdTotal > 0 && (
  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20, marginBottom: 24 }}>
  <h2 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: '#111827', textTransform: 'uppercase', letterSpacing: 0.4 }}>
  Down Payment
  </h2>
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
  <span style={{ fontSize: 13, color: '#6b7280' }}>Borrower Purchase Price</span>
  <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{fmt$(emd.borrowerPurchasePrice)}</span>
  </div>
  <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 6, paddingTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
  <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>Down Payment to Wire</span>
  <span style={{ fontSize: 17, fontWeight: 800, color: '#3a6ad9' }}>{fmt$(emd.emdRemaining)}</span>
  </div>
  {emd.offerFeeCredit > 0 && (
  <p style={{ margin: '10px 0 0', fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
  Your {emd.offerFeeCredit.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })} Purchase Initiation Deposit will be credited toward your purchase on your Closing Disclosure at closing.
  </p>
  )}
  </div>
  )}

  {/* Tasks */}
  <div className="space-y-4 mb-8">
  {/* Task 1: Appraisal */}
  <TaskCard
  number="1"
  title="Home Appraisal"
  subtitle="An appraiser determines the fair market value of the property"
  status={appraisalStatus}
  >
  {appraisalStatus === 'complete' ? (
  <div className="mt-2">
  {record?.appraisal_report_text && (
  <div className="bg-green-100 rounded-xl p-4">
  <p className="text-xs font-semibold text-green-700 mb-1 uppercase tracking-wider">Appraiser Notes</p>
  <p className="text-sm text-green-800">{record.appraisal_report_text}</p>
  </div>
  )}
  </div>
  ) : (
  <div className="mt-2 bg-blue-50 rounded-xl p-4 text-sm text-blue-800">
  Doorly orders your appraisal automatically once your down payment is received. The appraisal cost is paid at closing — it's disclosed on your Loan Estimate and Closing Disclosure.
  </div>
  )}
  </TaskCard>

  {/* Task 2: Inspection */}
  <TaskCard
  number="2"
  title="Home Inspection"
  subtitle="A certified inspector evaluates the property's condition"
  status={inspectionStatus}
  >
  {inspectionStatus === 'complete' ? (
  <div className="mt-2">
  {record?.inspection_report_text && (
  <div className="bg-green-100 rounded-xl p-4">
  <p className="text-xs font-semibold text-green-700 mb-1 uppercase tracking-wider">Inspector Notes</p>
  <p className="text-sm text-green-800">{record.inspection_report_text}</p>
  </div>
  )}
  </div>
  ) : (
  <div className="mt-2 bg-blue-50 rounded-xl p-4 text-sm text-blue-800">
  Doorly orders and pays for your inspection — nothing needed from you. You're welcome to arrange your own separate inspection at your expense.
  </div>
  )}
  </TaskCard>

  {/* Task 3: Review Reports */}
  <TaskCard
  number="3"
  title="Review Your Reports"
  subtitle="Review your appraisal package findings"
  status={reviewLocked ? 'locked' : reviewStatus === 'complete' ? 'complete' : 'unpaid'}
  >
  {reviewLocked ? (
  <p className="text-sm text-gray-400 mt-2">Complete your appraisal package first.</p>
  ) : reviewStatus === 'complete' ? (
  <p className="text-sm text-green-700 mt-2 font-medium">You've confirmed review of both reports.</p>
  ) : (
  <div className="mt-3">
  <p className="text-sm text-gray-600 mb-3">Both reports are ready. Review them with your advisor, then confirm below.</p>
  <button
  onClick={handleReviewComplete}
  disabled={reviewing}
  className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50 hover:opacity-90 transition-opacity"
  style={{ background: 'linear-gradient(135deg, #6d28d9 0%, #4f46e5 100%)' }}
  >
  {reviewing ? 'Saving…' : 'I\'ve Reviewed the Reports'}
  </button>
  </div>
  )}
  </TaskCard>

  {/* Task 4: Request Seller Credit */}
  <TaskCard
  number="4"
  title="Request Seller Credit"
  subtitle="Optionally request a credit from the seller based on findings"
  status={creditLocked ? 'locked' : credits.length > 0 ? 'complete' : 'unpaid'}
  >
  {creditLocked ? (
  <p className="text-sm text-gray-400 mt-2">Complete report review first.</p>
  ) : credits.length > 0 ? (
  <div className="mt-3 space-y-2">
  {credits.map((cr) => (
  <div key={cr.id} className="bg-white border border-gray-200 rounded-xl p-4">
  <div className="flex items-center justify-between mb-1">
  <span className="font-semibold text-black text-sm">{fmt$(cr.amount)} credit requested</span>
  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
  cr.status === 'approved' ? 'bg-green-100 text-green-700' :
  cr.status === 'denied' ? 'bg-red-100 text-red-700' :
  'bg-yellow-100 text-yellow-700'
  }`}>
  {cr.status.charAt(0).toUpperCase() + cr.status.slice(1)}
  </span>
  </div>
  <p className="text-xs text-gray-500">{cr.reason}</p>
  {cr.admin_notes && <p className="text-xs text-gray-600 mt-1 italic">{cr.admin_notes}</p>}
  </div>
  ))}
  <button
  onClick={() => setModal('credit')}
  className="text-sm text-purple-600 font-medium hover:text-purple-800 mt-1"
  >
  + Submit another request
  </button>
  </div>
  ) : (
  <div className="mt-3 flex items-center justify-between gap-4 flex-wrap">
  <p className="text-sm text-gray-600">Found issues? Request the seller to cover repair costs.</p>
  <button
  onClick={() => setModal('credit')}
  className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white hover:opacity-90 transition-opacity"
  style={{ background: 'linear-gradient(135deg, #6d28d9 0%, #4f46e5 100%)' }}
  >
  Request Credit
  </button>
  </div>
  )}
  </TaskCard>
  </div>

  {/* AI Summary */}
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-8">
  <div className="flex items-center justify-between mb-4">
  <div className="flex items-center gap-2">
  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #6d28d9 0%, #4f46e5 100%)' }}>
  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
  </svg>
  </div>
  <h2 className="font-bold text-black text-sm">AI Option Period Summary</h2>
  </div>
  {!aiSummary && (
  <button
  onClick={fetchAiSummary}
  disabled={aiLoading || !record}
  className="text-xs font-semibold text-purple-600 hover:text-purple-800 disabled:opacity-50"
  >
  {aiLoading ? 'Generating…' : 'Generate Summary'}
  </button>
  )}
  </div>
  {aiLoading ? (
  <div className="flex items-center gap-3 py-4">
  <svg className="animate-spin w-5 h-5 text-purple-500" fill="none" viewBox="0 0 24 24">
  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
  <span className="text-sm text-gray-500">Claude is analyzing your option period…</span>
  </div>
  ) : aiSummary ? (
  <div className="prose prose-sm max-w-none">
  <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">{aiSummary}</p>
  </div>
  ) : (
  <p className="text-gray-400 text-sm">
  {record ? 'Click "Generate Summary" to get an AI-powered overview of your option period.' : 'Available once your option period is set up.'}
  </p>
  )}
  </div>

  {/* Decision Section */}
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
  <h2 className="font-bold text-black text-sm mb-1">Make Your Decision</h2>
  <p className="text-gray-500 text-sm mb-5">
  {proceedLocked
  ? 'Your reports are being prepared. Nothing is needed from you right now — we\u2019ll let you know as soon as they\u2019re ready to review.'
  : alreadyDecided
  ? `You have chosen to ${record.decision === 'move_forward' ? 'proceed to closing' : 'cancel the contract'}.`
  : 'Based on your appraisal package, how would you like to proceed?'}
  </p>

  {!alreadyDecided && (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
  {/* Proceed */}
  <button
  onClick={() => handleDecision('move_forward')}
  disabled={!!deciding || proceedLocked}
  title={proceedLocked ? 'Available once your reports are ready to review.' : undefined}
  className="border-2 border-green-200 bg-green-50 rounded-2xl p-5 text-left hover:border-green-400 transition-colors disabled:opacity-50"
  >
  <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center mb-3">
  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
  </div>
  <p className="font-bold text-green-800 text-sm mb-1">
  {deciding === 'move_forward' ? 'Processing…' : 'Proceed to Closing'}
  </p>
  <p className="text-green-700 text-xs">I'm satisfied with the property and ready to move forward.</p>
  </button>

  {/* Cancel */}
  <button
  onClick={() => handleDecision('terminate')}
  disabled={!!deciding}
  className="border-2 border-red-200 bg-red-50 rounded-2xl p-5 text-left hover:border-red-400 transition-colors disabled:opacity-50"
  >
  <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center mb-3">
  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
  </div>
  <p className="font-bold text-red-800 text-sm mb-1">
  {deciding === 'terminate' ? 'Processing…' : 'Cancel Contract'}
  </p>
  <p className="text-red-700 text-xs">I want to cancel within the option period and receive my earnest money back.</p>
  </button>
  </div>
  )}

  {alreadyDecided && (
  <div className={`rounded-xl p-4 ${record.decision === 'move_forward' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
  <p className={`font-semibold text-sm ${record.decision === 'move_forward' ? 'text-green-800' : 'text-red-800'}`}>
  {record.decision === 'move_forward'
  ? '✓ You chose to proceed. Your loan is moving to the closing stage.'
  : '✗ You requested contract cancellation. Your advisor will contact you shortly.'}
  </p>
  </div>
  )}
  </div>

  {/* Nav */}
  <div className="flex flex-col sm:flex-row gap-3">
  <Link
  to="/portal/under-contract"
  className="px-6 py-3.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-50 text-center transition-colors"
  >
  ← Under Contract
  </Link>
  <Link
  to="/dashboard"
  className="px-6 py-3.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-50 text-center transition-colors"
  >
  Back to Dashboard
  </Link>
  </div>
  </div>
  )}
  </AppShell>
  );
}
